import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { ApolloProvider } from '@apollo/client/react';
import { store } from './store';
import { apolloClient } from './lib/apolloClient';
import { bootstrapTheme } from './lib/themeBootstrap';
import { startUsageEventBatching } from './lib/usageEventBatch';
import Router from './router';
import './index.css';

// Apply the persisted theme to <html data-theme> before React renders so there
// is no light/dark flash on reload (issue #424).
bootstrapTheme();

// Start the usage-event batch flush timer + unload/visibility listeners
// (issue #1451, part of epic #1448) — route-change/feature-click events
// queue via `trackEvent` from the moment the app boots.
startUsageEventBatching();

// Tyler Forge CSS must be imported first
import '@tylertech/forge/dist/forge.css';

// Tyler Forge icon registry — must be populated before components render
import { IconRegistry } from '@tylertech/forge';
import {
  tylIconAccountCircle,
  tylIconAccountTree,
  tylIconAdd,
  tylIconAddCircle,
  tylIconAdminPanelSettings,
  tylIconAnalytics,
  tylIconArchitecture,
  tylIconArchive,
  tylIconArrowBack,
  tylIconArrowDropDown,
  tylIconArrowDropUp,
  tylIconArrowForward,
  tylIconArrowRight,
  tylIconAssignment,
  tylIconAutorenew,
  tylIconBarChart,
  tylIconBatchPrediction,
  tylIconBusiness,
  tylIconCategory,
  tylIconChartArc,
  tylIconCheck,
  tylIconCheckCircle,
  tylIconChecklist,
  tylIconClose,
  tylIconCloud,
  tylIconCloudOff,
  tylIconCorporateFare,
  tylIconDashboard,
  tylIconDataMatrix,
  tylIconDelete,
  tylIconDeveloperBoard,
  tylIconDns,
  tylIconDomain,
  tylIconDownload,
  tylIconDragIndicator,
  tylIconEdit,
  tylIconError,
  tylIconExpandLess,
  tylIconExpandMore,
  tylIconExport,
  tylIconFactory,
  tylIconFlag,
  tylIconFolder,
  tylIconGauge,
  tylIconGrid,
  tylIconGridView,
  tylIconGroup,
  tylIconGroups,
  tylIconGroupWork,
  tylIconHeartPulse,
  tylIconHistory,
  tylIconHub,
  tylIconImport,
  tylIconInfo,
  tylIconInsights,
  tylIconLabel,
  tylIconLayers,
  tylIconLink,
  tylIconLock,
  tylIconLogout,
  tylIconMemory,
  tylIconMenu,
  tylIconOpenInFull,
  tylIconOpenInNew,
  tylIconPerson,
  tylIconPlayArrow,
  tylIconPreview,
  tylIconPriorityHigh,
  tylIconPublic,
  tylIconRefresh,
  tylIconRemove,
  tylIconSearch,
  tylIconSend,
  tylIconSettings,
  tylIconShowChart,
  tylIconStorage,
  tylIconSwapHoriz,
  tylIconTrendingFlat,
  tylIconTune,
  tylIconUndo,
  tylIconUploadFile,
  tylIconVerified,
  tylIconViewList,
  tylIconVpnKey,
  tylIconWarning,
} from '@tylertech/tyler-icons';

IconRegistry.define([
  tylIconAccountCircle,
  tylIconAccountTree,
  tylIconAdd,
  tylIconAddCircle,
  tylIconAdminPanelSettings,
  tylIconAnalytics,
  tylIconArchitecture,
  tylIconArchive,
  tylIconArrowBack,
  tylIconArrowDropDown,
  tylIconArrowDropUp,
  tylIconArrowForward,
  tylIconArrowRight,
  tylIconAssignment,
  tylIconAutorenew,
  tylIconBarChart,
  tylIconBatchPrediction,
  tylIconBusiness,
  tylIconCategory,
  tylIconChartArc,
  tylIconCheck,
  tylIconCheckCircle,
  tylIconChecklist,
  tylIconClose,
  tylIconCloud,
  tylIconCloudOff,
  tylIconCorporateFare,
  tylIconDashboard,
  tylIconDataMatrix,
  tylIconDelete,
  tylIconDeveloperBoard,
  tylIconDns,
  tylIconDomain,
  tylIconDownload,
  tylIconDragIndicator,
  tylIconEdit,
  tylIconError,
  tylIconExpandLess,
  tylIconExpandMore,
  tylIconExport,
  tylIconFactory,
  tylIconFlag,
  tylIconFolder,
  tylIconGauge,
  tylIconGrid,
  tylIconGridView,
  tylIconGroup,
  tylIconGroups,
  tylIconGroupWork,
  tylIconHistory,
  { name: 'monitor_heart', data: tylIconHeartPulse.data },
  tylIconHeartPulse,
  tylIconHub,
  tylIconImport,
  tylIconInfo,
  tylIconInsights,
  tylIconLabel,
  tylIconLayers,
  tylIconLink,
  tylIconLock,
  tylIconLogout,
  tylIconMemory,
  tylIconMenu,
  tylIconOpenInFull,
  tylIconOpenInNew,
  tylIconPerson,
  tylIconPlayArrow,
  tylIconPreview,
  tylIconPriorityHigh,
  tylIconPublic,
  tylIconRefresh,
  tylIconRemove,
  tylIconSearch,
  tylIconSend,
  tylIconSettings,
  tylIconShowChart,
  tylIconStorage,
  tylIconSwapHoriz,
  tylIconTrendingFlat,
  tylIconTune,
  tylIconUndo,
  tylIconUploadFile,
  tylIconVerified,
  tylIconViewList,
  tylIconVpnKey,
  tylIconWarning,
]);

// Dev-time guard: warn loudly if any nav icon name is unregistered (renders blank).
import { warnUnregisteredNavIcons } from './lib/iconRegistryGuard';

if (import.meta.env.DEV) {
  warnUnregisteredNavIcons();
}

// Tyler Forge components - import core components first
import '@tylertech/forge/core';
import '@tylertech/forge/label';
import '@tylertech/forge/scaffold';
import '@tylertech/forge/toolbar';
import '@tylertech/forge/app-bar';
import '@tylertech/forge/drawer';
import '@tylertech/forge/list';
import '@tylertech/forge/button';
import '@tylertech/forge/icon-button';
import '@tylertech/forge/icon';
import '@tylertech/forge/card';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/checkbox';
import '@tylertech/forge/divider';
import '@tylertech/forge/banner';
import '@tylertech/forge/dialog';

declare global {
  interface Window {
    __store?: typeof store;
  }
}

if (import.meta.env.DEV || import.meta.env.VITE_OKTA_DEV_MODE === 'true') {
  window.__store = store;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <ApolloProvider client={apolloClient}>
        <Router />
      </ApolloProvider>
    </Provider>
  </React.StrictMode>
);
